import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

function AdminUserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch('/.netlify/functions/admin-get-users', {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch users');

        const data = await response.json();
        setUsers(data.users || []);
      } catch (err) {
        console.error('User list fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Apply filter
  const filterUser = (u) => {
    const p = u.products || {};
    const pass = p.pass || {};
    const vb = p.voice_brand || {};
    const tda = p.tda || {};

    const anyReportReady =
      pass.report_status === 'completed' ||
      (vb.completed_count || 0) > 0 ||
      (tda.completed_count || 0) > 0;

    const anyInProgress =
      (pass.owned && pass.report_status !== 'completed') ||
      (vb.owned && (vb.completed_count || 0) < (vb.reports?.length || 0)) ||
      (tda.owned && (tda.completed_count || 0) < (tda.reports?.length || 0)) ||
      (vb.owned && (vb.reports?.length || 0) === 0) ||
      (tda.owned && (tda.reports?.length || 0) === 0);

    switch (filter) {
      case 'in_progress': return anyInProgress;
      case 'has_report': return anyReportReady;
      case 'pass_only': return pass.owned && !vb.owned && !tda.owned;
      case 'voice_brand': return vb.owned;
      case 'tda': return tda.owned;
      case 'all':
      default:
        return true;
    }
  };

  // Filter by search + status filter
  const filtered = users.filter(u => {
    if (!filterUser(u)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.full_name && u.full_name.toLowerCase().includes(q)) ||
      (u.profile_title && u.profile_title.toLowerCase().includes(q))
    );
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ? 1 : -1;
    return 0;
  });

  if (loading) return <p style={styles.loading}>Loading users...</p>;
  if (error) return <p style={styles.error}>Error: {error}</p>;

  return (
    <div>
      <div style={styles.headerRow}>
        <h1 style={styles.title}>Users ({filtered.length})</h1>
        <div style={styles.controls}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterSelect}
            title="Filter by status"
          >
            <option value="all">All customers</option>
            <option value="in_progress">In progress</option>
            <option value="has_report">Has a finished report</option>
            <option value="pass_only">PASS Profile only</option>
            <option value="voice_brand">Voice-Brand owners</option>
            <option value="tda">TDA owners</option>
          </select>
          <input
            type="text"
            placeholder="Search by name, email, or profile title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <SortHeader field="full_name" label="Name" sortField={sortField} sortAsc={sortAsc} onClick={handleSort} />
              <SortHeader field="email" label="Email" sortField={sortField} sortAsc={sortAsc} onClick={handleSort} />
              <th style={styles.th}>Products</th>
              <SortHeader field="stage" label="Stage" sortField={sortField} sortAsc={sortAsc} onClick={handleSort} />
              <th style={styles.th}>Reports</th>
              <SortHeader field="created_at" label="Created" sortField={sortField} sortAsc={sortAsc} onClick={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(user => (
              <tr key={user.id} style={styles.row}>
                <td style={styles.cell} onClick={() => navigate(`/admin/users/${user.id}`)}>
                  {user.full_name || <span style={styles.muted}>—</span>}
                  {user.is_admin && <span style={styles.adminTag}>admin</span>}
                </td>
                <td style={styles.cell} onClick={() => navigate(`/admin/users/${user.id}`)}>
                  {user.email}
                </td>
                <td style={styles.cell} onClick={() => navigate(`/admin/users/${user.id}`)}>
                  <ProductBadges products={user.products} />
                </td>
                <td style={styles.cell} onClick={() => navigate(`/admin/users/${user.id}`)}>
                  <StageText stage={user.stage} />
                </td>
                <td style={styles.cell}>
                  <ReportLinks products={user.products} />
                </td>
                <td
                  style={{ ...styles.cell, ...styles.dateCell }}
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  {user.created_at
                    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...styles.cell, textAlign: 'center', color: '#6B7A8F' }}>
                  {search || filter !== 'all' ? 'No users match.' : 'No users found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortHeader({ field, label, sortField, sortAsc, onClick }) {
  const isActive = sortField === field;
  const arrow = isActive ? (sortAsc ? ' ▲' : ' ▼') : '';
  return (
    <th
      style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onClick(field)}
    >
      {label}{arrow}
    </th>
  );
}

function ProductBadges({ products }) {
  if (!products) return <span style={styles.muted}>—</span>;
  const items = [];
  if (products.pass?.owned) items.push({ key: 'PASS', bg: '#E8F5E9', color: '#1B5E20' });
  if (products.voice_brand?.owned) items.push({ key: 'Voice-Brand', bg: '#E3F2FD', color: '#0D47A1' });
  if (products.tda?.owned) items.push({ key: 'TDA', bg: '#FCE4EC', color: '#AD1457' });
  if (items.length === 0) return <span style={styles.muted}>none</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.map(i => (
        <span key={i.key} style={{ ...styles.productBadge, backgroundColor: i.bg, color: i.color }}>
          {i.key}
        </span>
      ))}
    </div>
  );
}

function StageText({ stage }) {
  if (!stage) return <span style={styles.muted}>—</span>;
  const ready = stage.includes('ready');
  const generating = stage.includes('generating');
  const done = stage.includes('done');
  let color = '#6B7A8F';
  if (ready) color = '#1B5E20';
  else if (generating) color = '#E65100';
  else if (done) color = '#1565C0';
  return <span style={{ fontSize: 13, fontWeight: 500, color }}>{stage}</span>;
}

function ReportLinks({ products }) {
  if (!products) return <span style={styles.muted}>—</span>;
  const links = [];

  if (products.pass?.report_status === 'completed' && products.pass?.report_url) {
    links.push({ label: 'PASS', url: products.pass.report_url, bg: '#1B5E20' });
  }
  (products.voice_brand?.reports || []).forEach((r, idx) => {
    if (r.status === 'completed' && r.report_url) {
      const label = r.label ? `VB: ${r.label}` : `VB ${idx + 1}`;
      links.push({ label, url: r.report_url, bg: '#0D47A1' });
    }
  });
  (products.tda?.reports || []).forEach((r, idx) => {
    if (r.status === 'completed' && r.report_url) {
      const label = r.label ? `TDA: ${r.label}` : `TDA ${idx + 1}`;
      links.push({ label, url: r.report_url, bg: '#AD1457' });
    }
  });

  if (links.length === 0) return <span style={styles.muted}>—</span>;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {links.map((l, i) => (
        <a
          key={i}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...styles.reportLink, backgroundColor: l.bg }}
          onClick={(e) => e.stopPropagation()}
          title={`Open ${l.label} report`}
        >
          {l.label} ↓
        </a>
      ))}
    </div>
  );
}

const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: 0,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    gap: 16,
    flexWrap: 'wrap',
  },
  controls: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  filterSelect: {
    padding: '10px 14px',
    border: '1px solid #D0D5DD',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    backgroundColor: 'white',
    cursor: 'pointer',
    minWidth: 200,
  },
  searchInput: {
    padding: '10px 16px',
    border: '1px solid #D0D5DD',
    borderRadius: 8,
    fontSize: 14,
    width: 320,
    outline: 'none',
    fontFamily: 'inherit',
  },
  loading: {
    color: '#6B7A8F',
    fontSize: 15,
  },
  error: {
    color: '#D32F2F',
    fontSize: 15,
  },
  tableWrapper: {
    background: 'white',
    borderRadius: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 12,
    fontWeight: 600,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '2px solid #E8ECF0',
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: '1px solid #F0F2F5',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  cell: {
    padding: '12px 16px',
    verticalAlign: 'middle',
  },
  dateCell: {
    whiteSpace: 'nowrap',
    color: '#6B7A8F',
    fontSize: 13,
  },
  muted: {
    color: '#B0B8C4',
  },
  adminTag: {
    display: 'inline-block',
    marginLeft: 8,
    background: '#0B3D2E',
    color: 'white',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    verticalAlign: 'middle',
  },
  productBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  reportLink: {
    display: 'inline-block',
    padding: '4px 9px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    color: 'white',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
};

export default AdminUserList;
